import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Ability, subject } from '@casl/ability';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import {
  FetchEquipmentFilterRequest,
  FetchLaborFilterRequest,
  FetchSubContractorFilterRequest,
} from '../../../../../../../../core/projects.actions';
import {
  dailyReport,
  equipmentFilter,
  laborFilter,
  subContractorFilter,
} from '../../../../../../../../core/projects.selectors';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { Util } from '../../../../../../../../../../shared/helpers/util';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from '../../../../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { DailyReportService } from '../../../../daily-report.service';
import { LaborEquipmentLineItemComponent } from '../labor-equipment-line-item/labor-equipment-line-item.component';

type EntityType = 'labor' | 'equipment';

/** Fields entered once per crew that flow into every new row (see crewDefaults). */
const CASCADE_FIELDS = ['sub_contractor', 'duration', 'idle_duration', 'comment'] as const;
type CascadeField = typeof CASCADE_FIELDS[number];

interface LineItem {
  id: string | number;
  type: string;
  /** The saved entry this row edits (spread back into the payload), or the new-row seed. */
  original: any;
  /** Unsaved row appended by the picker this session (vs. loaded from the report in edit mode). */
  isNew: boolean;
  /** Cascade fields the user has edited on this row; they no longer follow the crew default. */
  ownedFields: Set<CascadeField>;
}

/**
 * Multi-line entry panel for Basic labor and equipment.
 *
 * Mirrors mobile's AddMultiLaborScreen and the web item-postings-details
 * pattern: one accordion per entry, exactly one open (`selectedLineItemId`,
 * the first row of the batch the picker just appended), the type
 * multi-select is an ADD control that resets after every pass, and a
 * single Save emits every row in one array with `linesToDelete` for removals.
 *
 * Add mode (`initialEntry` null): starts empty. Edit mode: loads every existing
 * entry of `entityType` from the report and opens `initialEntry`.
 */
@Component({
  standalone: false,
  selector: 'app-multi-labor-equipment-details',
  templateUrl: './multi-labor-equipment-details.component.html',
  styleUrls: ['./multi-labor-equipment-details.component.scss'],
})
export class MultiLaborEquipmentDetailsComponent implements OnInit, OnDestroy {
  @Input() entityType: EntityType = 'labor';
  @Input() initialEntry: any | null = null;
  @ViewChildren(LaborEquipmentLineItemComponent) rowComponents: QueryList<LaborEquipmentLineItemComponent>;

  lineItems: LineItem[] = [];
  lineItemForms: { [id: string]: FormGroup } = {};
  selectedLineItemId: string | number | null = null;
  linesToDelete: Array<string | number> = [];
  errorMessages: { [id: string]: { [control: string]: string } } = {};

  typePicker = new FormGroup({
    types: new FormControl<string[]>([]),
  });

  typeOptions: any[] = [];
  subContractorOptions: any[] = [];
  editable = true;

  private projectId: string;
  private reportData: any;
  private existingDataLoaded = false;
  private newIdCounter = 0;
  private saveRequested = false;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  /**
   * Crew defaults: users typically add several types for one contractor's crew
   * at once, so contractor, hours, hours idle and comment entered on any NEW row become the crew
   * default, which every new row projects into fields the user hasn't edited
   * on that row (`ownedFields`). Editing a field, blank or not, makes it the
   * user's. A default lives while some new row still holds it; when none does
   * (blanked, or the row was deleted) it falls back to a value another new row
   * holds, else it is dropped. That keeps "1" → "11" typed keystroke by
   * keystroke in sync, never overwrites something typed, and never refills a
   * field the user cleared on purpose. Saved rows (edit mode) neither send nor
   * receive defaults.
   */
  private crewDefaults: Partial<Record<CascadeField, unknown>> = {};

  private readonly errorMessageMap = {
    type: { required: 'This field is required.' },
    quantity: { required: 'This field is required.' },
  };

  constructor(
    private store: Store<fromRoot.State>,
    private router: Router,
    private dialog: MatDialog,
    private dailyReportService: DailyReportService,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private ability: Ability,
  ) {
  }

  get isEquipment(): boolean {
    return this.entityType === 'equipment';
  }

  get entityLabel(): string {
    return this.isEquipment ? 'equipment' : 'labor';
  }

  get heading(): string {
    return `${this.initialEntry ? 'Edit' : 'Add'} ${this.entityLabel}`;
  }

  ngOnInit() {
    // Same URL parse the single-entry forms used: /projects/<projectId>/...
    this.projectId = this.router.url.split('/')[2];

    const payload = {
      projectId: this.projectId,
      qp: { start: 0, total: 10000, include: [''] },
    };
    this.store.dispatch(FetchSubContractorFilterRequest({ payload }));
    this.store.dispatch(this.isEquipment
      ? FetchEquipmentFilterRequest({ payload })
      : FetchLaborFilterRequest({ payload }));

    this.store.select(subContractorFilter)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.subContractorOptions = data || [];
      });

    this.store.select(this.isEquipment ? equipmentFilter : laborFilter)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.typeOptions = data || [];
      });

    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.reportData = report;
        this.editable = this.ability.can('create', subject('DailyReport', { ...report }));
        this.loadExistingLineItems();
        this.applyEditable();
      });

    this.dailyReportService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.save());

    // The toolbar "..." Delete (actions-section.delete()) already confirmed
    // via its own dialog before emitting; go straight to removal here so the
    // user isn't asked twice.
    this.dailyReportService.triggerDelete
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.selectedLineItemId !== null) {
          this.removeLineItem(this.selectedLineItemId);
        }
      });

    // Add mode has no selectedId to flip on save, so daily-report.component's
    // usual "close the right panel on save" never fires for it; close it
    // ourselves once the post-save refetch comes back. Edit mode already
    // closes elsewhere, so leave it alone here.
    this.dailyReportService.fetchDailyReportById
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (!this.saveRequested) {
          return;
        }
        this.saveRequested = false;
        if (!this.initialEntry) {
          this.projectsService.closeRightPanel.next();
        }
      });

    // Precedent from the deleted old-labor-format-type-form: emit only in add
    // mode, where nothing is saved yet for the toolbar "..." Delete to act on.
    // Edit mode leaves the menu alone so its toolbar Delete keeps working.
    if (!this.initialEntry) {
      this.dailyReportService.disableOptionMenu.next(true);
    }
  }

  // ---------------------------------------------------------------- picker

  /** Called when the type multi-select panel closes: append a row per tick, then clear the ticks. */
  onTypePickerClosed() {
    const picked: string[] = this.typePicker.controls['types'].value || [];
    if (!picked.length) {
      return;
    }
    this.addLineItems(picked);
    this.typePicker.controls['types'].setValue([], { emitEvent: false });
  }

  /**
   * A custom type was typed into the picker or a row's type dropdown. Keep it in
   * the shared option list so every dropdown (picker and each row) can render it;
   * the dropdown that created it only knows about it privately.
   */
  onTypeAdded(option: { value: any; label: string }) {
    if (!option || this.typeOptions.some(o => o.value === option.value)) {
      return;
    }
    this.typeOptions = [option, ...this.typeOptions];
  }

  /** Append one row per type and open the FIRST of the new batch (the top one the user reads first). */
  addLineItems(types: string[]) {
    if (!types.length) {
      return;
    }
    let firstNewId: string | null = null;
    types.forEach(type => {
      const id = `new_${new Date().getTime()}_${this.newIdCounter++}`;
      const original = {
        id,
        type,
        sub_contractor: null,
        quantity: null,
        duration: null,
        idle_duration: null,
        comment: '',
        new_labor_format: false,
        ...(this.isEquipment ? { model: null, is_rental: false } : {}),
      };
      const row: LineItem = { id, type, original, isNew: true, ownedFields: new Set<CascadeField>() };
      this.lineItems.push(row);
      this.lineItemForms[id] = this.buildForm(original);
      this.watchCrewDefaults(row);
      firstNewId = firstNewId ?? id;
    });
    // Seed the batch from the crew defaults already entered. `original` keeps
    // `sub_contractor: null` so resolveSubContractor() can't mistake a seeded id
    // for a saved contractor object.
    this.applyCrewDefaults();
    this.selectedLineItemId = firstNewId;
    this.dailyReportService.disableAddMenu.next(false);
    this.projectsService.isAnyFormDirty.next(true);
    this.scrollSelectedIntoView();
  }

  /**
   * Scroll the open row to the top of the panel once it has rendered. Without
   * this a row appended below the fold gives no visible feedback that anything
   * happened, and a clicked entry deep in a long edit list opens off-screen.
   */
  private scrollSelectedIntoView() {
    setTimeout(() => {
      const row = this.rowComponents?.find(r => r.lineItem?.id === this.selectedLineItemId);
      row?.scrollIntoView();
    });
  }

  // ------------------------------------------------------------- accordion

  selectLineItem(id: string | number) {
    this.selectedLineItemId = this.selectedLineItemId === id ? null : id;
    // + button (future pictures/location): enabled only while a row is open.
    this.dailyReportService.disableAddMenu.next(this.selectedLineItemId === null);
  }

  deleteLineItem(id: string | number) {
    if (!this.editable) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel('Delete', 'Are you sure you want to delete?'),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }
      this.removeLineItem(id);
    });
  }

  checkForErrors(id: string | number, controlName?: string) {
    const form = this.lineItemForms[id];
    if (!form) {
      return;
    }
    if (controlName) {
      delete this.errorMessages[id]?.[controlName];
    }
    this.errorMessages[id] = {
      ...(this.errorMessages[id] || {}),
      ...this.commonService.checkFormValidation(form, this.errorMessageMap, controlName),
    };
  }

  // ------------------------------------------------------------------ save

  save() {
    const firstInvalid = this.lineItems.find(li => this.lineItemForms[li.id]?.invalid);
    if (firstInvalid) {
      this.lineItems.forEach(li => this.checkForErrors(li.id));
      this.selectedLineItemId = firstInvalid.id;
      this.dailyReportService.disableAddMenu.next(false);
      return;
    }

    const data = this.lineItems.map(li => {
      const { sub_contractor, ...rest } = this.lineItemForms[li.id].getRawValue();
      const contractor = this.resolveSubContractor(sub_contractor, li.original?.sub_contractor);
      return {
        ...li.original,
        ...rest,
        sub_contractor: contractor,
        sub_contractor_id: contractor?.id ?? null,
      };
    });

    this.saveRequested = true;
    this.dailyReportService.save.next({
      type: this.entityType,
      data,
      options: { linesToDelete: this.linesToDelete },
    });
  }

  // --------------------------------------------------------------- private

  /**
   * The user's edits to a NEW row's contractor / hours take ownership of that
   * field and update the crew default. Projection writes use `emitEvent: false`,
   * so this only ever fires for the user's own edits.
   */
  private watchCrewDefaults(row: LineItem) {
    const form = this.lineItemForms[row.id];
    CASCADE_FIELDS.forEach(field => {
      form.controls[field].valueChanges
        .pipe(takeUntil(this.onDestroy))
        .subscribe(value => {
          row.ownedFields.add(field);
          if (Util.isEmpty(value)) {
            this.reconcileCrewDefault(field);
          } else {
            this.crewDefaults[field] = value;
          }
          this.applyCrewDefaults();
        });
    });
  }

  /** Project the crew defaults into every new row's fields the user hasn't edited. */
  private applyCrewDefaults() {
    this.lineItems
      .filter(li => li.isNew)
      .forEach(li => {
        (Object.keys(this.crewDefaults) as CascadeField[]).forEach(field => {
          const control = this.lineItemForms[li.id].controls[field];
          if (!li.ownedFields.has(field) && control.value !== this.crewDefaults[field]) {
            control.setValue(this.crewDefaults[field], { emitEvent: false });
          }
        });
      });
  }

  /**
   * Keep a crew default only while some new row still holds it; otherwise adopt a
   * non-empty value another new row holds, and failing that drop it.
   */
  private reconcileCrewDefault(field: CascadeField) {
    const values = this.lineItems
      .filter(li => li.isNew)
      .map(li => this.lineItemForms[li.id].controls[field].value);
    if (values.includes(this.crewDefaults[field])) {
      return;
    }
    const fallback = values.find(value => !Util.isEmpty(value));
    if (Util.isEmpty(fallback)) {
      delete this.crewDefaults[field];
    } else {
      this.crewDefaults[field] = fallback;
    }
  }

  private removeLineItem(id: string | number) {
    if (!this.editable) {
      return;
    }
    const row = this.lineItems.find(li => li.id === id);
    this.lineItems = this.lineItems.filter(li => li.id !== id);
    delete this.lineItemForms[id];
    delete this.errorMessages[id];
    if (row && !row.isNew) {
      this.linesToDelete = [...this.linesToDelete, id];
    }
    CASCADE_FIELDS.forEach(field => this.reconcileCrewDefault(field));
    if (this.selectedLineItemId === id) {
      this.selectedLineItemId = null;
      this.dailyReportService.disableAddMenu.next(true);
    }
    this.projectsService.isAnyFormDirty.next(true);
  }

  private loadExistingLineItems() {
    if (this.existingDataLoaded || !this.reportData || !this.initialEntry) {
      return;
    }
    this.existingDataLoaded = true;

    const key = this.isEquipment ? 'equipments' : 'labors';
    const existing: any[] = this.reportData?.reportDetails?.[key] || [];

    existing.forEach(entry => {
      this.lineItems.push({ id: entry.id, type: entry.type, original: entry, isNew: false, ownedFields: new Set<CascadeField>() });
      this.lineItemForms[entry.id] = this.buildForm(entry);
    });

    const clicked = existing.find(e => e.id === this.initialEntry?.id);
    this.selectedLineItemId = clicked ? clicked.id : null;
    this.dailyReportService.disableAddMenu.next(this.selectedLineItemId === null);
    if (clicked) {
      this.scrollSelectedIntoView();
    }

    // Building the forms emits valueChanges; clear the dirty flag once that settles.
    setTimeout(() => this.projectsService.isAnyFormDirty.next(false));
  }

  private buildForm(entry: any): FormGroup {
    const controls: { [k: string]: FormControl } = {
      type: new FormControl(entry?.type ?? null, [Validators.required]),
      sub_contractor: new FormControl(entry?.sub_contractor?.id ?? null),
      quantity: new FormControl(entry?.quantity ?? null, [Validators.required]),
      duration: new FormControl(entry?.duration ?? null),
      idle_duration: new FormControl(entry?.idle_duration ?? null),
      comment: new FormControl(entry?.comment ?? null),
    };
    if (this.isEquipment) {
      controls['model'] = new FormControl(entry?.model ?? null);
      controls['is_rental'] = new FormControl(!!entry?.is_rental);
    }
    const form = new FormGroup(controls);
    form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.projectsService.isAnyFormDirty.next(true));
    if (!this.editable) {
      form.disable({ emitEvent: false });
    }
    return form;
  }

  /**
   * Resolve the form's `sub_contractor` id against the loaded options. If the
   * id isn't in `subContractorOptions` (inactive/soft-deleted contractor, the
   * project's list exceeds the fetch cap, or save landing before the async
   * filter response) but it still matches the row's original contractor,
   * keep that original object rather than silently wiping it.
   */
  private resolveSubContractor(subContractorId: string | number | null, original: any): any {
    const match = this.subContractorOptions.find(o => o.id === subContractorId);
    if (match) {
      return match;
    }
    if (original && subContractorId === original.id) {
      return original;
    }
    return null;
  }

  private applyEditable() {
    Object.values(this.lineItemForms).forEach(form => {
      if (this.editable) {
        form.enable({ emitEvent: false });
      } else {
        form.disable({ emitEvent: false });
      }
    });
    if (this.editable) {
      this.typePicker.enable({ emitEvent: false });
    } else {
      this.typePicker.disable({ emitEvent: false });
    }
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
